import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { documentRequests, documents } from '../services/api';

const TABS = [
  { key: '', label: 'All' },
  { key: 'paid', label: 'Paid — awaiting fulfillment' },
  { key: 'pending_payment', label: 'Pending payment' },
  { key: 'fulfilled', label: 'Fulfilled' },
  { key: 'declined', label: 'Declined' },
];

const STATUS_STYLES = {
  pending_payment: 'bg-gray-100 text-gray-600',
  paid: 'bg-amber-100 text-amber-800',
  fulfilled: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

const REQUEST_TYPE_LABELS = {
  form_b: 'Form B',
  form_f: 'Form F',
  specific_documents: 'Specific documents',
};

function FulfillModal({ req, onClose, onDone }) {
  const [buildingDocs, setBuildingDocs] = useState([]);
  const [documentId, setDocumentId] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    documents.list({ building_id: req.building_id }).then((res) => setBuildingDocs(res.data)).catch(() => {});
  }, [req.building_id]);

  const attachExisting = async () => {
    if (!documentId) { toast.error('Choose a document'); return; }
    setBusy(true);
    try {
      await documentRequests.fulfill(req.id, { document_id: documentId });
      toast.success('Request fulfilled');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to fulfill');
    } finally {
      setBusy(false);
    }
  };

  const generateNow = async () => {
    setBusy(true);
    try {
      await documentRequests.fulfill(req.id, { generate: req.request_type, fields: {} });
      toast.success('Certificate generated and sent');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">Fulfill Request</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div className="text-sm text-gray-600">
            <p><span className="font-medium text-gray-900">{req.requester_name}</span> ({req.requester_role})</p>
            <p>{REQUEST_TYPE_LABELS[req.request_type]} · {req.building_name}{req.unit_number ? ` · Unit ${req.unit_number}` : ''}</p>
          </div>

          {(req.request_type === 'form_b' || req.request_type === 'form_f') && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <p className="text-sm text-blue-900 font-medium mb-1">Generate now</p>
              <p className="text-xs text-blue-700 mb-3">
                Uses this building's saved certificate defaults. For full control over the fields,
                use the Certificate Generator page instead and attach the result below.
              </p>
              <button onClick={generateNow} disabled={busy}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-lg disabled:opacity-60">
                {busy ? 'Working…' : `Generate ${REQUEST_TYPE_LABELS[req.request_type]} & Send`}
              </button>
            </div>
          )}

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Attach an existing document</p>
            <select value={documentId} onChange={(e) => setDocumentId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3">
              <option value="">Select a document…</option>
              {buildingDocs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <button onClick={attachExisting} disabled={busy || !documentId}
              className="w-full border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2 rounded-lg disabled:opacity-50">
              {busy ? 'Working…' : 'Attach & Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DocumentRequests() {
  const [tab, setTab] = useState('paid');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fulfillTarget, setFulfillTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    documentRequests.list(tab ? { status: tab } : {})
      .then((res) => setRows(res.data))
      .catch(() => toast.error('Failed to load requests'))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const handleDecline = async (req) => {
    const reason = window.prompt('Reason for declining (optional):') || '';
    try {
      await documentRequests.decline(req.id, reason);
      toast.success('Request declined');
      load();
    } catch {
      toast.error('Failed to decline');
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Document Requests</h1>
        <p className="text-gray-500 text-sm mt-1">Requests from realtors, lawyers, and notaries via the public portal.</p>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center text-gray-400">
          No requests here.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Requester</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Building / Unit</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Fee</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="text-gray-900 font-medium">{r.requester_name}</p>
                      <p className="text-xs text-gray-400">{r.requester_company || r.requester_role} · {r.requester_email}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {r.building_name}{r.unit_number ? ` · Unit ${r.unit_number}` : ''}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {REQUEST_TYPE_LABELS[r.request_type]}
                      {r.request_type === 'specific_documents' && r.manager_notes && (
                        <p className="text-xs text-gray-400 mt-0.5 max-w-[220px] truncate" title={r.manager_notes}>
                          {r.manager_notes}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-900 font-medium">${r.fee_amount.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[r.status]}`}>
                        {r.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {r.status === 'paid' && (
                        <>
                          <button onClick={() => setFulfillTarget(r)}
                            className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium px-3 py-1.5 rounded-lg mr-2">
                            Fulfill
                          </button>
                          <button onClick={() => handleDecline(r)}
                            className="text-xs text-red-500 hover:underline">Decline</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {fulfillTarget && (
        <FulfillModal
          req={fulfillTarget}
          onClose={() => setFulfillTarget(null)}
          onDone={() => { setFulfillTarget(null); load(); }}
        />
      )}
    </div>
  );
}
